import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, money, formatApiErrorDetail } from "../../lib/api";
import { MatrixStepper } from "../../components/GymLayout";
import { startStripeCheckout } from "../PaymentSuccess";
import { ShippingOptions } from "../../components/ShippingOptions";
import { Input } from "../../components/ui/input";


export default function WholesaleOrderForm() {
  const { productId } = useParams();
  const location = useLocation();
  const prefill = location.state?.prefill;

  const [product, setProduct] = useState(null);
  const [color, setColor] = useState(prefill?.color || null);
  const [notes, setNotes] = useState("");
  const [quantities, setQuantities] = useState(prefill?.quantities || {});
  const [submitting, setSubmitting] = useState(false);
  const [logos, setLogos] = useState({ paragon: true, academy: true });
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", zip: "" });
  const [shipOpt, setShipOpt] = useState(null);
  const setA = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  useEffect(() => {
    api.get(`/products/${productId}`).then(({ data }) => {
      setProduct(data);
      if (!color) setColor(data.colors?.[0] || null);
    });
  }, [productId]);

  if (!product) return <div className="text-zinc-500 font-mono py-32 text-center">Loading…</div>;

  const prices = product.wholesale_size_prices || {};
  const totalUnits = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
  const totalPrice = product.sizes.reduce((s, sz) => s + (quantities[sz] || 0) * (prices[sz] ?? product.wholesale_price ?? 0), 0);
  const moqReached = !product.moq || totalUnits >= product.moq;
  const sizesSel = product.sizes.filter((s) => quantities[s] > 0).map((s) => ({ size: s, qty: quantities[s] }));
  const quoteItems = sizesSel.length ? [{ product_id: product.id, color, sizes: sizesSel }] : [];
  const shipping = shipOpt?.price ?? 0;
  const feePct = product.wholesale_card_fee_pct ?? 3;
  const cardFee = Math.round((totalPrice + shipping) * feePct) / 100;
  const grandTotal = Math.round((totalPrice + shipping + cardFee) * 100) / 100;

  const submit = async () => {
    if (totalUnits === 0) return toast.error("Enter quantities for at least one size");
    if (!moqReached) return toast.error(`Minimum order is ${product.moq} units (any mix of sizes)`);
    if (!addr.line1 || !addr.city || !addr.state || !addr.zip) return toast.error("Please enter the ship-to address");
    if (!shipOpt) return toast.error("Please choose a shipping option");
    const sizes = sizesSel;
    setSubmitting(true);
    try {
      const { data } = await api.post("/wholesale/orders", {
        items: [{
          product_id: product.id,
          color,
          sizes,
          customization: { notes, paragon_logo: logos.paragon, academy_logo: logos.academy },
        }],
        notes,
        shipping_address: addr,
        shipping_service: shipOpt.service,
      });
      toast.success(`Order ${data.order_number} created. Redirecting to secure payment…`);
      await startStripeCheckout(data, "wholesale");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Order failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="px-5 lg:px-8 py-8 max-w-5xl">
      <Link to="/gym/catalog" className="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-400 font-mono text-sm mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to Catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="aspect-square bg-[#121216] border border-white/10 rounded-xl overflow-hidden">
            <img src={product.images?.front} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <h1 className="font-heading font-black uppercase text-3xl text-white mt-4">{product.name}</h1>
          <p className="font-mono text-emerald-400 font-bold mt-1">{money(product.wholesale_price)} wholesale · Min {product.moq || 0} units (any sizes)</p>

          <div className="mt-5">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Product Color</p>
            <div className="flex flex-wrap gap-2">
              {product.colors?.map((c) => (
                <button key={c} onClick={() => setColor(c)} className={`px-4 py-2 rounded-sm text-sm border ${color === c ? "border-emerald-400 text-emerald-400 bg-emerald-400/10" : "border-white/15 text-zinc-300"}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Academy Branding</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <button onClick={() => setLogos((l) => ({ ...l, paragon: !l.paragon }))} className={`px-3 py-2 rounded-sm text-xs border ${logos.paragon ? "border-yellow-400 text-yellow-400 bg-yellow-400/10" : "border-white/15 text-zinc-400"}`}>Paragon Master Logo</button>
              <button onClick={() => setLogos((l) => ({ ...l, academy: !l.academy }))} className={`px-3 py-2 rounded-sm text-xs border ${logos.academy ? "border-emerald-400 text-emerald-400 bg-emerald-400/10" : "border-white/15 text-zinc-400"}`}>Academy Logo</button>
            </div>
            <p data-testid="standard-placement-note" className="text-xs text-zinc-500">All gear uses the standard Paragon design placement.</p>
          </div>
        </div>

        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Bulk Size Quantities</p>
          <MatrixStepper
            sizes={product.sizes}
            sizePrices={prices}
            quantities={quantities}
            onChange={(s, v) => setQuantities((q) => ({ ...q, [s]: v }))}
          />

          <textarea
            data-testid="wholesale-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Customization instructions / artwork notes…"
            className="w-full mt-4 bg-[#0A0A0C] border border-white/15 rounded-md p-3 text-sm text-white outline-none focus:border-emerald-400/50"
            rows={3}
          />

          <div className="bg-[#121216] border border-white/10 rounded-lg p-5 mt-4" data-testid="wholesale-ship-section">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-3">Ship To</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <Input data-testid="wholesale-ship-line1" placeholder="Address" value={addr.line1} onChange={setA("line1")} className="col-span-2 bg-[#0A0A0C] border-white/15" />
              <Input data-testid="wholesale-ship-city" placeholder="City" value={addr.city} onChange={setA("city")} className="bg-[#0A0A0C] border-white/15" />
              <Input data-testid="wholesale-ship-state" placeholder="State" value={addr.state} onChange={setA("state")} className="bg-[#0A0A0C] border-white/15" />
              <Input data-testid="wholesale-ship-zip" placeholder="ZIP" value={addr.zip} onChange={setA("zip")} className="bg-[#0A0A0C] border-white/15" />
            </div>
            {quoteItems.length ? <ShippingOptions items={quoteItems} zip={addr.zip} value={shipOpt} onChange={setShipOpt} accent="green" /> : <p className="text-sm text-zinc-500">Add quantities to see shipping.</p>}
          </div>

          <div className="bg-[#121216] border border-white/10 rounded-lg p-5 mt-4">
            <div className="flex justify-between text-zinc-300"><span>Total Units</span><span data-testid="wholesale-total-units" className="font-mono font-bold text-white">{totalUnits}</span></div>
            <div className="flex justify-between text-zinc-300 mt-1"><span>Subtotal</span><span data-testid="wholesale-subtotal" className="font-mono text-white">{money(totalPrice)}</span></div>
            <div className="flex justify-between text-zinc-300 mt-1"><span>Shipping{shipOpt ? ` (${shipOpt.label})` : ""}</span><span data-testid="wholesale-shipping" className="font-mono text-white">{shipOpt ? money(shipping) : "—"}</span></div>
            <div className="flex justify-between text-zinc-300 mt-1"><span>Card processing fee ({feePct}%)</span><span data-testid="wholesale-card-fee" className="font-mono text-white">{money(cardFee)}</span></div>
            <div className="flex justify-between text-zinc-300 mt-1"><span>Order Total · no sales tax</span><span data-testid="wholesale-total-price" className="font-mono font-bold text-emerald-400 text-lg">{money(grandTotal)}</span></div>
            {!moqReached && (
              <p data-testid="wholesale-moq-warning" className="font-mono text-xs text-yellow-400 mt-2">MINIMUM {product.moq} UNITS (ANY MIX OF SIZES) · {totalUnits} / {product.moq}</p>
            )}
            <button data-testid="wholesale-submit" disabled={submitting || !moqReached || !shipOpt} onClick={submit} className="btn-green w-full px-6 py-3.5 rounded-sm mt-4 disabled:opacity-50">
              {submitting ? "Redirecting to secure payment…" : `Pay ${money(grandTotal)} with Card`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
